<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { getCurrentInstance } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import VmixTokenService from '@/services/VmixTokenService'
import FormButton from '@/components/Atoms/FormButton.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { track, EVENTS } from '@/lib/tracking'

const MODAL_ID = 'modalDeleteVmixToken'

const props = defineProps({
	vmixToken: {
		type: Object,
		default: () => ({}),
	},
	modalSize: {
		type: String,
		default: 'md',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	cancelText: {
		type: String,
		default: 'Cancel',
	},
})

const emit = defineEmits(['modal-cancel', 'modal-dismiss', 'toggle-processing', 'token-deleted'])

const root = getCurrentInstance()?.proxy?.$root
const { notify } = useNotification()

const { isVisible, hide } = useLegacyModalBridge(MODAL_ID)

const dismiss = () => {
	emit('modal-cancel')
	hide()
	emit('modal-dismiss')
}

const deleteToken = async () => {
	try {
		emit('toggle-processing', true)
		hide()
		await VmixTokenService.deleteVmixToken(props.vmixToken._id || props.vmixToken.id)
		track(EVENTS.VMIX_TOKEN_DELETED, {})
		notify({ group: 'success', text: 'vMix token is deleted' })
		emit('token-deleted')
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't delete vMix token",
			text: err.message,
		})

		if (err.message?.includes('upgrade')) {
			showModal('billing-prompt')
		}
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<h4 class="text-s-xl">Delete vMix Token</h4>
			<p class="text-s-s text-dark-9 mb-2 mt-2">
				The "{{ vmixToken.name }}" token access will become invalid for all users. This action cannot be undone.
			</p>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="surface-4" size="md" @click="dismiss">
					{{ cancelText }}
				</FormButton>
				<FormButton type="red" size="md" @click="deleteToken">
					Revoke
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
